import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Maximize2, FileDown, ExternalLink, X } from 'lucide-react';

export const CreativesPortfolioSection = () => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Toujours stocker la version normalisée
  const [activeCategory, setActiveCategory] = useState('tous');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const previewUrlRef = useRef(null);
  const lastGoodItemsRef = useRef([]);


  // Définir la fonction de chargement en dehors du useEffect
  const loadPortfolio = async () => {
    setIsLoading(true);
    try {
      const cacheBuster = Date.now();
      const response = await fetch(`/creatives/index.json?v=${cacheBuster}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setPortfolioItems(data);
      lastGoodItemsRef.current = data;
      setLoadError(null);
    } catch (error) {
      console.error('Erreur lors du chargement du portfolio:', error);
      setLoadError(
        "Impossible de charger le portfolio (connexion ou serveur de dev). Les éléments existants restent affichés."
      );
      // Conserver la dernière version connue pour éviter un écran vide
      if (lastGoodItemsRef.current.length) {
        setPortfolioItems(lastGoodItemsRef.current);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Charger au premier montage uniquement
  useEffect(() => {
    loadPortfolio();
  }, []);

  const safeItems = Array.isArray(portfolioItems) ? portfolioItems : [];
  // Normalisation pour le filtrage uniquement
  const normalize = (str) => (str || '').toString().trim().toLowerCase();
  // Liste des vraies catégories, mais on stocke la version normalisée pour le filtre
  // Génère une liste [{label, value}] pour chaque catégorie
  const categories = [
    { label: 'Tous', value: 'tous' },
    ...Array.from(new Set(safeItems.map((item) => item.category)))
      .map((cat) => ({ label: cat, value: normalize(cat) }))
  ];
  const filteredItems =
    activeCategory === 'tous'
      ? safeItems
      : safeItems.filter((item) => normalize(item.category) === activeCategory);
  const displayItems = filteredItems;

  // Fermer la modal si on change de catégorie (évite les états incohérents)
  useEffect(() => {
    setSelectedItem(null);
  }, [activeCategory]);

  // Pour les PDF, on récupère un blob pour éviter les requêtes Range qui posent souci en dev server
  useEffect(() => {
    // Libère le blob précédent
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    const loadPdfBlob = async () => {
      if (!selectedItem || selectedItem.type !== 'pdf') {
        setPreviewUrl(null);
        return;
      }
      try {
        const cacheBuster = Date.now();
        const resp = await fetch(`${selectedItem.src}?v=${cacheBuster}`, { cache: 'no-store' });
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      } catch (err) {
        console.error('Erreur lors du chargement du PDF:', err);
        setPreviewUrl(null);
      }
    };

    loadPdfBlob();
  }, [selectedItem]);

  // Nettoyage global quand le composant est démonté
  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section className="py-24 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-[#181C25] via-[#1a1f2b] to-[#181C25]">
      <div className="container mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-montserrat font-bold text-white mb-4">
            Portfolio <span style={{ color: '#0090FF', textShadow: '0 0 20px rgba(0, 144, 255, 0.3)' }}>Créatif</span>
          </h2>
        
        </motion.div>

        <div className="flex flex-wrap gap-3 justify-center mb-10">
          {categories.map((cat) => (
            <button
              key={cat.value}
              onClick={() => setActiveCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                activeCategory === cat.value
                  ? 'bg-blue-600/90 border-blue-400 text-white shadow-lg shadow-blue-600/30'
                  : 'bg-white/5 border-white/10 text-gray-300 hover:border-blue-400/50 hover:text-white'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        <motion.div
          key={activeCategory}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
        >
          {isLoading && <p className="text-center text-gray-400 col-span-full">Chargement des créations…</p>}
          {loadError && (
            <p className="text-center text-amber-300 col-span-full text-sm">
              {loadError}
            </p>
          )}
          {!isLoading && displayItems.length === 0 && (
            <p className="text-center text-gray-400 col-span-full">Aucune création dans cette catégorie.</p>
          )}

          {displayItems.map((item) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              className="group relative overflow-hidden rounded-xl cursor-pointer h-36 sm:h-40 md:h-44 lg:h-48"
              onClick={() => setSelectedItem(item)}
            >
              <div className="relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 overflow-hidden flex items-center justify-center">
                {item.type === 'image' ? (
                  <img
                    src={item.src}
                    alt={item.title}
                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <img
                    src="/assets/pdf-thumb.png"
                    alt="Aperçu PDF"
                    className="w-3/4 h-3/4 object-contain opacity-80"
                  />
                )}
                <div className="absolute bottom-2 left-2 right-2 flex flex-col items-start">
                  <span className="bg-black/60 text-xs text-white px-2 py-0.5 rounded mb-1 truncate max-w-full">{item.title}</span>
                  <span className="bg-blue-500/80 text-xs text-white px-2 py-0.5 rounded">{item.category}</span>
                </div>
              </div>

              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center">
                <Maximize2 size={40} className="text-white mb-3" />
                <p className="text-white font-semibold text-center px-4">{item.description || 'Voir le document'}</p>
              </div>

              <div className="absolute top-4 right-4 bg-blue-500/90 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs font-semibold">
                {item.category}
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <p className="text-gray-400 max-w-2xl mx-auto mb-6">
            Vous avez un projet créatif ? Contactez-nous pour discuter de vos besoins en design graphique, création
            d'affiches, brochures ou identité visuelle complète.
          </p>
        </motion.div>
      </div>

      {selectedItem && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedItem(null)}
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto"
          style={{ overscrollBehavior: 'contain' }}
        >
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="bg-gray-900 rounded-2xl border border-gray-700 p-4 sm:p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="bg-gray-800 rounded-lg mb-6 flex items-center justify-center overflow-auto border border-gray-700 cursor-pointer"
              style={{ minHeight: '40vh', maxHeight: '70vh' }}
              onClick={() => window.open(selectedItem.src, '_blank')}
              title="Ouvrir en grand dans un nouvel onglet"
            >
              {selectedItem.type === 'image' ? (
                <img
                  src={selectedItem.src}
                  alt={selectedItem.title}
                  className="max-h-[65vh] w-full object-contain"
                />
              ) : previewUrl ? (
                <object
                  data={previewUrl}
                  type="application/pdf"
                  className="w-full h-[65vh]"
                  aria-label={selectedItem.title}
                >
                  <p className="text-gray-300 p-4">Aperçu indisponible. Cliquez pour ouvrir le PDF dans un nouvel onglet.</p>
                </object>
              ) : (
                <p className="text-gray-300 p-6 text-center">
                  Chargement du PDF… Cliquez pour ouvrir dans un nouvel onglet.
                </p>
              )}
            </div>
            <button
              onClick={() => setSelectedItem(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 z-10 bg-black/40"
              title="Fermer la fenêtre"
              style={{lineHeight:0}}
            >
              <X size={28} />
            </button>
            <h3 className="text-2xl font-montserrat font-bold text-white mb-2 mt-2">{selectedItem.title}</h3>
            <p className="text-blue-400 font-semibold mb-4">{selectedItem.category}</p>
            {selectedItem.description && (
              <p className="text-gray-300 mb-6">
                {selectedItem.description}
              </p>
            )}
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default CreativesPortfolioSection;
